import { assignableRoles, normalizeRole, ROLE_INFO, type Role } from '@console/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { UserPlus } from 'lucide-react'
import { useState } from 'react'
import { Button } from '../../components/ui/Button'
import { Dropdown } from '../../components/ui/Dropdown'
import { Modal } from '../../components/ui/Modal'
import { Badge, ErrorText, Field, Spinner } from '../../components/ui/misc'
import { useToast } from '../../components/ui/toast'
import { useActiveOrg, useMe } from '../../lib/api'
import { authClient } from '../../lib/auth'
import { fmtDate } from '../../lib/format'
import { Section } from './SettingsLayout'

const roleOption = (r: Role) => ({ id: r, label: ROLE_INFO[r].label, hint: ROLE_INFO[r].description })

export function MembersPage() {
  const { org, canManage, role: myRole } = useActiveOrg()
  const { data: me } = useMe()
  const qc = useQueryClient()
  const toast = useToast()
  const [inviteOpen, setInviteOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('developer')
  const [err, setErr] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['org-full', org?.id],
    enabled: Boolean(org),
    queryFn: async () => {
      const r = await authClient.organization.getFullOrganization({ query: { organizationId: org!.id } })
      if (r.error) throw r.error
      return r.data
    },
  })
  const refresh = () => qc.invalidateQueries({ queryKey: ['org-full'] })

  const invite = async () => {
    setBusy(true)
    setErr(null)
    const { error } = await authClient.organization.inviteMember({ email: email.trim().toLowerCase(), role, organizationId: org!.id })
    setBusy(false)
    if (error) return setErr(error)
    toast(`Invitation sent to ${email}`)
    setInviteOpen(false)
    setEmail('')
    void refresh()
  }

  const changeRole = async (memberId: string, r: Role) => {
    const { error } = await authClient.organization.updateMemberRole({ memberId, role: r, organizationId: org!.id })
    if (error) return toast(error.message ?? 'Could not change role', 'error')
    toast('Role updated')
    void refresh()
  }

  const remove = async () => {
    if (!removing) return
    const { error } = await authClient.organization.removeMember({ memberIdOrEmail: removing.id, organizationId: org!.id })
    setRemoving(null)
    if (error) return toast(error.message ?? 'Could not remove member', 'error')
    toast('Member removed')
    void refresh()
  }

  const cancelInvite = async (invitationId: string) => {
    await authClient.organization.cancelInvitation({ invitationId })
    void refresh()
  }

  if (isLoading || !data) return <Spinner />
  const pending = (data.invitations ?? []).filter((i) => i.status === 'pending')
  const roleOptions = assignableRoles(myRole).map(roleOption)

  return (
    <>
      <Section
        title="Members"
        actions={
          canManage && (
            <Button onClick={() => setInviteOpen(true)}>
              <UserPlus className="h-3.5 w-3.5" /> Invite member
            </Button>
          )
        }
      >
        <div className="-mx-3 overflow-x-auto">
          <table className="w-full min-w-[520px]">
            <thead className="border-b border-line">
              <tr>
                <th className="th">Name</th>
                <th className="th">Email</th>
                <th className="th">Role</th>
                <th className="th">Joined</th>
                <th className="th" />
              </tr>
            </thead>
            <tbody>
              {data.members.map((m) => {
                const isMe = m.userId === me?.user?.id
                const editable = canManage && !isMe && (myRole === 'owner' || m.role !== 'owner')
                return (
                  <tr key={m.id} className="border-b border-line-subtle last:border-0">
                    <td className="td font-medium">
                      {m.user.name} {isMe && <span className="text-ink-muted">(you)</span>}
                    </td>
                    <td className="td text-ink-secondary">{m.user.email}</td>
                    <td className="td">
                      {editable ? (
                        <Dropdown value={normalizeRole(m.role)} options={roleOptions} onChange={(r) => changeRole(m.id, r)} />
                      ) : (
                        <Badge tone={m.role === 'owner' ? 'brand' : 'neutral'}>{ROLE_INFO[normalizeRole(m.role)].label}</Badge>
                      )}
                    </td>
                    <td className="td text-ink-secondary">{fmtDate(m.createdAt)}</td>
                    <td className="td text-right">
                      {editable && (
                        <Button variant="ghost" className="!h-7 !text-[12px] text-danger" onClick={() => setRemoving({ id: m.id, name: m.user.name || m.user.email })}>
                          Remove
                        </Button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Section>

      {pending.length > 0 && (
        <Section title="Pending invitations">
          <div className="-mx-4">
            <table className="w-full">
              <tbody>
                {pending.map((i) => (
                  <tr key={i.id} className="border-b border-line-subtle last:border-0">
                    <td className="td">{i.email}</td>
                    <td className="td">
                      <Badge>{ROLE_INFO[normalizeRole(i.role)].label}</Badge>
                    </td>
                    <td className="td text-ink-secondary">expires {fmtDate(i.expiresAt)}</td>
                    <td className="td text-right">
                      {canManage && (
                        <Button variant="ghost" className="!h-7 !text-[12px]" onClick={() => cancelInvite(i.id)}>
                          Cancel
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      <Modal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title="Invite a member"
        footer={
          <>
            <Button variant="secondary" onClick={() => setInviteOpen(false)}>
              Cancel
            </Button>
            <Button onClick={invite} loading={busy} disabled={!/^\S+@\S+\.\S+$/.test(email)}>
              Send invitation
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field label="Email" hint="They get an email with a link that works for 7 days. They don't need beta access of their own.">
            <input className="input" type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <div>
            <span className="field-label">Role</span>
            <div className="space-y-1.5">
              {roleOptions.map((o) => (
                <label key={o.id} className={`flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2 ${role === o.id ? 'border-[color:var(--border-strong)] bg-surface-hover' : 'border-line'}`}>
                  <input type="radio" className="mt-1 accent-[color:var(--trodo-primary)]" checked={role === o.id} onChange={() => setRole(o.id)} />
                  <span>
                    <span className="block text-[13px] font-medium text-ink">{o.label}</span>
                    <span className="block text-[12px] text-ink-muted">{o.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <ErrorText error={err} />
        </div>
      </Modal>

      <Modal
        open={!!removing}
        onClose={() => setRemoving(null)}
        title="Remove member?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={remove}>
              Remove
            </Button>
          </>
        }
      >
        {removing?.name} will lose access to this organization. Keys they created keep working until you revoke them.
      </Modal>
    </>
  )
}
